import { m } from 'framer-motion';
import styled, { css } from 'styled-components';

import animation from '@helpers/animation';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { Device, from, until } from '@helpers/media';
import Star from '@img/icons/star.svg?url';
import { SearchableSelectStyles } from '@stories/Components/Forms/SearchableSelect/SearchableSelect.styles';
import { ButtonStyles } from '@stories/Elements/Forms/Buttons/Button.styles';
import { RatingStarsStyles } from '@stories/Elements/Misc/RatingStars/RatingStars.styles';
import { Typography } from '@stories/Elements/Typography/Typography.styles';
import { Grid } from '@stories/Settings/Grids/Grid.styles';

const OuterContainer = styled.section``;

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const SubTitle = styled.h2`
  ${Typography.H4Style};
  ${fonts.helvetica['87-heavy-condensed']};
  text-align: center;
  margin: 20px auto;
  max-width: 700px;
`;

const OverviewContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  padding: 32px 0;
  ${Grid.defaultGap};

  @media ${from(Device.TabletLarge)} {
    grid-template-columns: 1fr 1fr 1fr;
  }
`;

const AverageRatingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  padding: 20px;
  background-color: ${brand.white};
  border-radius: 4px;
`;

const AverageRating = styled.strong`
  ${fonts.helvetica['93-black-extended']}
  font-size: 75px;
  margin-right: 24px;
  color: ${brand.red.base};

  @media ${from(Device.MobileSmall)} {
    font-size: 60px;
  }

  @media ${from(Device.Desktop)} {
    font-size: 72px;
  }
`;

const AverageRatingStars = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Stars = styled.div<{ rating: number }>`
  display: grid;
  grid-auto-flow: column;
  gap: 3px;
  height: 17px;

  ${({ rating }) => css`
    ${StarWrapper}:nth-child(1)::after {
      width: ${rating < 10 ? rating * 10 : 100}%;
    }

    ${StarWrapper}:nth-child(2)::after {
      width: ${rating > 10 ? (rating < 20 ? (rating % 10) * 10 : 100) : 0}%;
    }

    ${StarWrapper}:nth-child(3)::after {
      width: ${rating > 20 ? (rating < 30 ? (rating % 10) * 10 : 100) : 0}%;
    }

    ${StarWrapper}:nth-child(4)::after {
      width: ${rating > 30 ? (rating < 40 ? (rating % 10) * 10 : 100) : 0}%;
    }

    ${StarWrapper}:nth-child(5)::after {
      width: ${rating > 40 ? (rating < 50 ? (rating % 10) * 10 : 100) : 0}%;
    }
  `}
`;

const StarWrapper = styled.div`
  position: relative;
  display: flex;
  z-index: 1;
  align-self: center;
  flex: 0 0 auto;
  height: 17px;

  > svg {
    fill: ${brand.grey[85]};

    width: 18px;
  }

  &::after {
    content: '';
    display: inline-block;
    position: absolute;
    top: 0;
    left: 0;
    background-color: ${brand.red.base};
    width: 0;
    height: 100%;
    mask-image: url(${Star});
    mask-size: 18px 17.1px;
    mask-repeat: no-repeat;
  }
`;

const ReviewCount = styled.small`
  ${fonts.helvetica['55-roman']}
  font-size: 17px;
  margin-top: 4px;

  span {
    font-weight: bold;
  }
`;

const RatingBreakdownContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  background-color: ${brand.red.base};
  border-radius: 4px;
`;

const RatingBreakdownRow = styled.div<{ isActive?: boolean; isTransparent?: boolean }>`
  display: flex;
  align-items: center;
  width: calc(100% - 27px);
  padding: 8px 0;
  cursor: pointer;
  transition: opacity 0.2s ${animation.easeInOutCubic};

  > div {
    display: flex;
    align-items: center;
    width: 30px;
    margin-right: 5px;
    flex-shrink: 0;
    color: ${brand.white};

    position: relative;

    small {
      ${fonts.helvetica['87-heavy-condensed']}
      font-size: 15px;
    }

    > svg {
      position: absolute;
      right: -3px;
      top: 50%;
      transform: translateY(-50%) translateX(-50%);
      transition: fill 0.2s ${animation.easeInOutCubic};
      fill: ${brand.white};

      width: 14px;
    }
  }

  &:hover {
    opacity: 1 !important;
  }

  ${({ isTransparent }) =>
    isTransparent &&
    css`
      opacity: 0.3;
    `}

  ${({ isActive }) =>
    isActive &&
    css`
      opacity: 1 !important;
      > div > svg {
        fill: ${brand.red.base};
      }
    `}
`;

const RatingBar = styled.div<{ quantity: number; fillPercentage: number }>`
  flex: 1;
  height: 8px;
  min-width: 50px;
  background-color: ${brand.black};
  margin-right: 20px;

  position: relative;

  &::after {
    content: '2';
    position: absolute;
    right: -34px;
    top: 50%;
    transform: translateY(-50%);

    ${fonts.helvetica['55-roman']};
    font-size: 13px;
  }

  &::before {
    content: '';
    width: 0;
    height: 100%;
    position: absolute;
    left: 0;
    top: 0;
    background-color: ${brand.white};

    transition: width 0.6s ${animation.easeInOutCubic};
  }

  ${({ quantity, fillPercentage }) => css`
    &::after {
      content: '${quantity}';
    }

    &::before {
      width: ${fillPercentage}%;
    }
  `}
`;

const RecommendedPercentageContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background-color: ${brand.white};
  border-radius: 4px;

  > p {
    ${fonts.helvetica['55-roman']}
    font-size: 14px;
    line-height: 18px;
  }

  @media ${from(Device.Desktop)} {
    > p {
      font-size: 18px;
      line-height: 20px;
    }
  }
`;

const RecommendedPercentage = styled.strong`
  ${fonts.helvetica['93-black-extended']}
  font-size: 50px;
  margin-right: 24px;
  padding-left: 12px;
  color: ${brand.red.base};

  @media ${from(Device.MobileSmall)} {
    font-size: 60px;
  }

  @media ${from(Device.Desktop)} {
    font-size: 72px;
  }
`;

const ControlsContainer = styled.div<{ centered?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
  gap: 20px;
  z-index: 2;

  @media ${until(Device.TabletLarge)} {
    ${ButtonStyles.ButtonElement} {
      order: 2;
    }
  }

  ${({ centered }) =>
    centered
      ? css`
          display: flex;
        `
      : css`
          @media ${from(Device.TabletLarge)} {
            display: grid;
            grid-template-columns: 1fr 1fr 1fr;
            grid-gap: 30px;
          }
        `};

  padding-bottom: 32px;
  border-bottom: 1px solid ${brand.grey[95]};
`;

const PaginationCount = styled.p`
  ${fonts.helvetica['55-roman']}
  font-size: 14px;
  display: flex;
  gap: 4px;
  order: 3;

  @media ${until(Device.TabletLarge)} {
    grid-row: 1;
  }
  @media ${from(Device.TabletLarge)} {
    order: initial;
    grid-column: 1 / span 3;
  }
`;

const FilterDropdownWrapper = styled.div`
  flex: 1;
  z-index: 1;
  align-self: stretch;
  margin-top: 3px;

  @media ${until(Device.TabletLarge)} {
    ${SearchableSelectStyles.Wrapper} {
      max-width: none;
    }
  }

  @media ${from(Device.TabletLarge)} {
    justify-content: flex-end;
    align-self: auto;
  }
`;

const FilterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  z-index: 2;

  ${SearchableSelectStyles.Wrapper} {
    min-width: 300px;
    padding-bottom: 3px;
    border-bottom: none;
  }

  ${SearchableSelectStyles.Dropdown} {
    border: 1px solid ${brand.grey[40]};
    border-radius: 4px;
    padding: 0;
  }

  ${SearchableSelectStyles.Option} {
    font-size: 15px;
    line-height: 22px;
    ${fonts.helvetica['53-extended']};
    font-weight: 400;
    background: ${brand.white};
    padding: 7px 16px;
    border-bottom: none;

    &:hover {
      background: #efefef;
    }
  }

  ${SearchableSelectStyles.Label} {
    display: none;
  }

  ${SearchableSelectStyles.Value} {
    ${fonts.helvetica['63-medium-extended']}
    font-size: 14px;
    background: ${brand.white};
    padding: 15px 20px;
    border: 1px solid ${brand.grey[40]};
    border-radius: 4px;

    &::after {
      right: 12px;
    }
  }

  small {
    font-size: 10px;
    color: ${brand.grey[45]};
  }
`;

const ReviewsOuterContainer = styled(m.div)`
  display: grid;
  grid-auto-columns: 1fr;
  ${Grid.defaultGap};
  border-top: 1px solid ${brand.grey[80]};
  border-bottom: 1px solid ${brand.grey[80]};
  padding: ${fluid(17, 22)} 0px;

  @media ${from(Device.DesktopSmall)} {
    grid-template-columns: 1fr 1fr 1fr;
  }
`;

const ReviewsFooter = styled.footer`
  display: flex;
  flex-direction: column;
  align-items: center;
  grid-column: span 3;
`;

const WriteAReviewButtonWrapper = styled(m.div).attrs({
  layout: true,
})``;

const ShowMoreButtonWrapper = styled.div`
  margin: 32px 0px;
`;

const Teaser = styled.span`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px;
  align-items: flex-end;
  margin-top: 4px;

  ${RatingStarsStyles.Stars} {
    margin-bottom: 0px;

    svg > path {
      fill: rgb(${brand.red.baseRgb});
    }
  }

  span {
    ${fonts.helvetica['73-bold-extended']};
    font-size: 12px;

    @media ${from(Device.Tablet)} {
      font-size: 14px;
    }
  }
`;

const Filters = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;

  &[data-align-left='true'] {
    justify-content: flex-start;
  }

  @media ${until(Device.TabletLarge)} {
    &[data-no-results='true'] {
      align-items: center;
    }
  }

  @media ${from(Device.TabletLarge)} {
    &[data-no-results='true'] {
      justify-content: flex-start;
    }
  }

  @media ${until(Device.Desktop)} {
    flex-direction: column;
  }
`;

const LightboxWrapper = styled(m.div).attrs(() => ({
  initial: 'hidden',
  animate: 'visible',
  exit: 'hidden',
  variants: {
    hidden: {
      opacity: 0,
    },
    visible: {
      opacity: 1,
    },
  },
  transition: {
    bounce: 0,
    ease: 'easeInOut',
  },
}))`
  position: fixed;
  inset: 0;
  top: 0;
  left: 0;
  z-index: 1000;
  background-color: rgba(0, 0, 0, 0.8);

  display: flex;
  justify-content: center;
  align-items: center;

  cursor: pointer;
`;

const LightboxWrapperInner = styled(m.div).attrs(() => ({
  variants: {
    hidden: {
      opacity: 0,
      y: 35,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        delay: 0.5,
      },
    },
  },
}))`
  cursor: unset;
  aspect-ratio: 16 / 9;
  width: 100%;
  max-width: 80dvw;
  background-color: rgba(0, 0, 0, 0.2);
  position: relative;

  [data-is-image-lightbox='true'] & {
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;

    img {
      max-height: 100%;
    }
  }
`;

const CloseButton = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  padding: 15px;
  cursor: pointer;
  width: 48px;
  height: 48px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${brand.black};
  z-index: 1;

  svg {
    width: 18px;
    height: 18px;
    color: ${brand.white};
  }
`;

const ButtonWrapper = styled.div`
  height: 100%;

  display: flex;
  justify-content: center;
  align-items: flex-end;

  ${ButtonStyles.ButtonElement} {
    width: max-content;
    max-width: 100%;
  }
`;

const LightboxButtons = styled.div`
  position: absolute;
  z-index: 2;
  width: 100%;
  height: 100%;

  button {
    ${ButtonReset};

    position: absolute;
    width: ${fluid(35, 50)};
    height: ${fluid(35, 50)};

    display: flex;
    justify-content: center;
    align-items: center;
    background-color: ${brand.white};

    svg {
      color: ${brand.black};
      width: 50%;
      height: 50%;
    }

    top: 50%;
    transform: translateY(-50%);

    &[data-direction='left'] {
      left: ${fluid(10, 20)};
    }

    &[data-direction='right'] {
      right: ${fluid(10, 20)};
    }
  }
`;

export const ProductReviewsListingStyles = {
  OuterContainer,
  Container,
  SubTitle,
  OverviewContainer,
  AverageRatingContainer,
  AverageRating,
  AverageRatingStars,
  Stars,
  StarWrapper,
  ReviewCount,
  RatingBreakdownContainer,
  RatingBreakdownRow,
  RatingBar,
  RecommendedPercentageContainer,
  RecommendedPercentage,
  ControlsContainer,
  PaginationCount,
  FilterDropdownWrapper,
  ReviewsOuterContainer,
  ReviewsFooter,
  ShowMoreButtonWrapper,
  WriteAReviewButtonWrapper,
  Teaser,
  Filters,
  FilterWrapper,
  LightboxWrapper,
  LightboxWrapperInner,
  CloseButton,
  ButtonWrapper,
  LightboxButtons,
};
